import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { BrandRevealProvider } from '@/context/BrandRevealContext';
import { useAuth } from '@/hooks/useApp';
import { Layout } from '@/components/Layout';
import { Spinner } from '@/components/ui';
import { ConnectionBanner, ErrorBoundary, UpdateBanner } from '@/components/AppShell';
import { ScrollRestoration } from '@/components/ScrollRestoration';
import { registerServiceWorker } from '@/lib/pwa';
import { CATEGORY_SEGMENT, BUY_GUIDE_SLUG, SELL_GUIDE_SLUG } from '@/lib/seo';
import { SearchPage } from '@/pages/SearchPage';
import { ListingDetailPage } from '@/pages/ListingDetailPage';
import { AuthCallbackPage, ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from '@/pages/AuthPages';
import { FavouritesPage, MyListingsPage, NotFoundPage } from '@/pages/AccountPages';
import { ProfilePage } from '@/pages/ProfilePage';
import { SellerProfilePage } from '@/pages/SellerProfilePage';
import { DeleteAccountInfoPage, PrivacyPage, TermsPage } from '@/pages/LegalPages';
import { SettingsPage } from '@/pages/SettingsPage';
import { ConversationPage, InboxPage } from '@/pages/MessagesPages';
import { CategoryPage, CategoryRootPage } from '@/pages/CategoryPages';
import { BuyGuidePage, SellGuidePage } from '@/pages/GuidePages';

// The listing form is large and only sellers ever open it, so it is not part of the bundle every
// visitor downloads to look at cars.
const CreateListingPage = lazy(() =>
  import('@/pages/CreateListingPage').then((module) => ({ default: module.CreateListingPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Listings change slowly relative to how fast people click around, so a short window of
      // reuse removes most refetches without ever showing meaningfully stale prices.
      staleTime: 30_000,
      // Refetching every time the tab regains focus is a lot of requests for a page whose contents
      // did not change while someone answered a message.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 401 or a 404 will not become a 200 by asking again.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  /*
    Held stable across renders. `Navigate` compares `state` by identity to decide whether to fire
    again, so an object literal built inline is a brand new redirect on every single re-render — and
    this component re-renders whenever the auth context does.
  */
  const from = useMemo(
    () => ({ from: location.pathname + location.search }),
    [location.pathname, location.search],
  );

  // The silent refresh on load has to settle first. Redirecting while it is in flight would bounce
  // a signed-in visitor to the login page on every hard refresh.
  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  if (!user) {
    /*
      The navigation controls already send signed-out visitors straight to the login page, so
      reaching here means a typed URL, a bookmark or a shared link. That still redirects — and the
      intended destination travels with it so signing in returns them to it rather than dumping them
      on the home page.

      The placeholder is rendered rather than nothing at all. `Navigate` redirects from an effect,
      so returning `null` outright hands the browser a frame with an empty page in it before the
      redirect lands, and the layout visibly collapses and springs back.
    */
    return (
      <>
        <div className="flex justify-center py-24">
          <Spinner className="size-8 text-accent" />
        </div>
        <Navigate to="/prijava" replace state={from} />
      </>
    );
  }

  return <>{children}</>;
}

/**
 * The application shell: everything that stays put while the page underneath it changes.
 *
 * Rendered as the layout route so each page arrives through `<Outlet />`. That matters beyond
 * tidiness — see the router below.
 */
function AppShellRoute() {
  const location = useLocation();

  return (
    // Inside the router because signing in needs to navigate.
    <AuthProvider>
      {/* Outside Layout so the marque wash covers the header and the tab bar too. */}
      <BrandRevealProvider>
        <Layout>
          <ScrollRestoration />

          <Suspense
            fallback={
              <div className="flex justify-center py-24">
                <Spinner className="size-8 text-accent" />
              </div>
            }
          >
            {/*
              Keyed by pathname so every navigation replays the entry animation. Deliberately not
              keyed by the search string: refining a filter must not re-animate the whole page
              underneath the person doing the refining.
            */}
            <div key={location.pathname} className="page-enter">
              {/*
                Inside the router rather than around it, so the recovery screen still has the
                header, the navigation and a working way back to the search.
              */}
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </div>
          </Suspense>
        </Layout>
      </BrandRevealProvider>
    </AuthProvider>
  );
}

/*
  A data router, and the routes are declared here rather than as a nested `<Routes>` element.

  That is not a stylistic preference. `useNavigate` picks its implementation from the nearest route
  context: inside a data route it calls `router.navigate` and can carry options like `viewTransition`
  through to the router; inside a plain `<Routes>` subtree it falls back to pushing straight onto
  history, and every such option is silently dropped. With the old shape, a `<Link viewTransition>`
  in the header animated and the identical link on a car card did not — the cards lived under a
  nested `<Routes>`, and nothing anywhere reported a problem.
*/
const router = createBrowserRouter([
  {
    element: <AppShellRoute />,
    children: [
      { path: '/', element: <SearchPage /> },
      { path: '/oglas/:shortCode', element: <ListingDetailPage /> },
      { path: '/prodavac/:sellerId', element: <SellerProfilePage /> },

      /*
        The pages that exist for search traffic.

        Their paths are Serbian and readable because the URL is itself a signal and, more usefully,
        because it is legible in a search result and in a forwarded message. They mirror the route
        table the server's SEO resolver knows about — the two have to stay in step, since the server
        writes the head for a URL it has never rendered.
      */
      { path: `/${CATEGORY_SEGMENT}`, element: <CategoryRootPage /> },
      { path: `/${CATEGORY_SEGMENT}/:makeSlug`, element: <CategoryPage /> },
      { path: `/${CATEGORY_SEGMENT}/:makeSlug/:modelSlug`, element: <CategoryPage /> },
      { path: `/${BUY_GUIDE_SLUG}`, element: <BuyGuidePage /> },
      { path: `/${SELL_GUIDE_SLUG}`, element: <SellGuidePage /> },

      { path: '/prijava', element: <LoginPage /> },
      { path: '/registracija', element: <RegisterPage /> },
      { path: '/prijava/povratak', element: <AuthCallbackPage /> },
      { path: '/zaboravljena-lozinka', element: <ForgotPasswordPage /> },
      { path: '/reset-lozinke', element: <ResetPasswordPage /> },

      /*
        Public on purpose, all three. A privacy policy behind a sign-in wall is not a published
        policy, and the deletion page in particular has to be readable by somebody who has already
        uninstalled the app — which is exactly the case Google Play requires it for.
      */
      { path: '/privatnost', element: <PrivacyPage /> },
      { path: '/uslovi', element: <TermsPage /> },
      { path: '/brisanje-naloga', element: <DeleteAccountInfoPage /> },

      {
        path: '/postavi-oglas',
        element: (
          <RequireAuth>
            <CreateListingPage />
          </RequireAuth>
        ),
      },
      {
        // The same component. An edit form that is a separate copy of the create form drifts from
        // it, and the seller finds out when a field they filled in yesterday cannot be corrected.
        path: '/uredi-oglas/:shortCode',
        element: (
          <RequireAuth>
            <CreateListingPage />
          </RequireAuth>
        ),
      },
      {
        path: '/moji-oglasi',
        element: (
          <RequireAuth>
            <MyListingsPage />
          </RequireAuth>
        ),
      },
      {
        path: '/profil',
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      {
        path: '/sacuvani',
        element: (
          <RequireAuth>
            <FavouritesPage />
          </RequireAuth>
        ),
      },
      {
        path: '/poruke',
        element: (
          <RequireAuth>
            <InboxPage />
          </RequireAuth>
        ),
      },
      {
        path: '/poruke/:conversationId',
        element: (
          <RequireAuth>
            <ConversationPage />
          </RequireAuth>
        ),
      },
      {
        path: '/podesavanja',
        element: (
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const [updateReady, setUpdateReady] = useState(false);

  // The callback only fires when a worker has installed *and* one was already controlling the page.
  // A first install is not news worth interrupting anyone for; a second one is a new version.
  useEffect(() => registerServiceWorker(() => setUpdateReady(true)), []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ConnectionBanner />
        <UpdateBanner visible={updateReady} />

        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
